import { useMemo } from 'react';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import BetViewUtils from 'modules/your-bets/bet-view/utils.js';
import OddsBoostIndicator from 'modules/odds-boost-indicator/index.js';
import MatchAccaSelectionItem from 'modules/match-acca/match-acca-selection-item/index.js';
import BonusIndicator from 'modules/bonus-indicator/index.js';
import BonusIndicatorTypes from 'modules/bonus-indicator/types.js';

const { FlexPane, Text, Image, ChangedValueIndicator, DateTime, VoidReasonMessage, BetStatusIcon } =
    MojitoPresentation.Components;
const { EventFormatter, MarketHelper, BettingHelper } = MojitoPresentation.Utils;
const { utils: BetsUtils, types: BetsTypes } = MojitoServices.Bets;
const { PRICE_TYPE, BET_TYPE } = BetsTypes;
const EventUtils = MojitoServices.SportsContent.Events.utils;

const BetLegsDetails = props => {
    const {
        mojitoTools: { config },
    } = props;

    return (
        <FlexPane class={'ta-BetLegsDetails'} config={config.container}>
            {props.bet.legs.map(leg => {
                return BetsUtils.isMatchAccaLeg(leg) ? (
                    <MatchAccaLeg key={getSelectionId(leg)} {...props} leg={leg} />
                ) : (
                    <DefaultLeg key={getSelectionId(leg)} {...props} leg={leg} />
                );
            })}
        </FlexPane>
    );
};

const DefaultLeg = props => {
    const { bet, leg, mojitoTools } = props;
    const { config, l10n } = mojitoTools;
    const { legInfo, status, priceType, voidReason } = leg;
    const { eventStartTime } = legInfo;
    const isMulticast = BetsUtils.isMulticast(leg);

    return (
        <FlexPane class={'ta-defaultLeg'} config={config.legsItemInfoContainer}>
            <FlexPane config={config.legsItemInfo} class="ta-legInfo">
                <LegStatusIcon config={config} status={status} />
                <FlexPane config={config.infoContainer}>
                    <FlexPane config={config.selectionContainer} class="ta-selectionContainer">
                        <Text config={config.selectionName} class="ta-selectionName">
                            {BettingHelper.getSelectionText(leg, l10n, isMulticast)}
                        </Text>
                        <BestOdds priceType={priceType} config={config} />
                        <Odds {...props} />
                    </FlexPane>
                    <Text config={config.marketName} class="ta-marketName">
                        {BettingHelper.getMarketNameOrEachWayLabel(
                            leg,
                            l10n,
                            bet.numberOfLines,
                            isMulticast
                        )}
                    </Text>
                    <Text config={config.eventName} class="ta-eventName">
                        <DateTime
                            config={config.eventNameDateTimeFormat}
                            dateTime={eventStartTime}
                            dateTimeRenderer={formatEventName}
                            rendererData={legInfo.eventName}
                        />
                    </Text>
                    <BetNotification {...props} />
                    <ExtraFields {...props} />
                </FlexPane>
            </FlexPane>
            <VoidReasonMessage config={config.voidReasonMessage} reason={voidReason} />
        </FlexPane>
    );
};

const MatchAccaLeg = props => {
    const { bet, leg, mojitoTools } = props;
    const { config, stringResolver } = mojitoTools;
    const { legInfo, baseOdds, status } = leg;
    const { eventName } = legInfo;

    const isOddsBoostBet = !!baseOdds;

    const header = isOddsBoostBet
        ? stringResolver.resolveString('$BETSLIP.MATCH_ACCA.BOOSTED_BET_HEADER')
        : stringResolver.resolveString('$BETSLIP.MATCH_ACCA.BET_HEADER');

    return (
        <FlexPane class={'ta-matchAccaLeg'} config={config.matchAccaInfo}>
            <LegStatusIcon config={config} status={status} />
            <FlexPane class={'ta-matchAccaLegInfo'} config={config.infoContainerMatchAcca}>
                <FlexPane config={config.selectionContainer}>
                    <FlexPane config={config.selectionNameContainer}>
                        <Text config={config.matchAccaHeaderText} class="ta-matchAcca-header">
                            {header}
                        </Text>
                    </FlexPane>
                    <Odds {...props} leg={leg} />
                </FlexPane>
                <Text config={config.matchAccaEventName} class="ta-matchAcca-eventName">
                    {eventName}
                </Text>
                {isOddsBoostBet && (
                    <OddsBoostIndicator config={config.oddsBoostIndicator} bet={bet} />
                )}
                <MatchAccaLegParts {...props} leg={leg} />
                <ExtraFields {...props} leg={leg} />
            </FlexPane>
        </FlexPane>
    );
};

const MatchAccaLegParts = props => {
    const { leg, mojitoTools } = props;
    const { stringResolver, config } = mojitoTools;
    return (
        <FlexPane class={'ta-matchAccaLegParts'} config={config.matchAccaLegParts}>
            {leg.parts.map((part, i, row) => {
                const showSeparator = i + 1 !== row.length;
                const { selectionName: partSelectionName, hcap = '', marketName } = part;
                const { name, handicapLabel } = MarketHelper.handleHandicap(
                    partSelectionName,
                    hcap
                );
                const selectionName =
                    name || stringResolver.resolveString('$BET_LEGS_DETAILS.VALUE_NOT_AVAILABLE');
                const selectionLabel = `${selectionName} ${handicapLabel}`.trim();

                return (
                    <MatchAccaSelectionItem
                        config={config.matchAccaSelectionItem}
                        showSeparator={showSeparator}
                        selectionLabel={selectionLabel}
                        marketName={marketName}
                        earlyPayout={part.earlyPayout}
                        voidReason={part.voidReason}
                        key={part.selectionId}
                        status={part.status}
                    />
                );
            })}
        </FlexPane>
    );
};

const BestOdds = ({ priceType, config }) =>
    priceType === PRICE_TYPE.BP && <Image config={config.bestOddsIcon} class="ta-bestOddsIcon" />;

const Odds = props => {
    const { leg, mojitoTools } = props;
    const { config, l10n } = mojitoTools;
    const { odds, baseOdds, priceType, startingPrice } = leg;

    const oddsInfo = createOddsInfo(priceType, odds, baseOdds, startingPrice, l10n);
    const oddsChangeIndicatorConfig =
        priceType === PRICE_TYPE.SP ? config.startingPriceIndicator : config.priceBoostIndicator;

    return oddsInfo.previousValue ? (
        <ChangedValueIndicator
            config={oddsChangeIndicatorConfig}
            previousValue={oddsInfo.previousValue}
            newValue={oddsInfo.value}
        />
    ) : (
        <Text config={config.oddsLabel} class="ta-priceText">
            {oddsInfo.value}
        </Text>
    );
};

const createOddsInfo = (priceType, odds, baseOdds, startingPrice, l10n) => {
    return priceType === PRICE_TYPE.SP && startingPrice
        ? {
              value: startingPrice,
              previousValue: EventUtils.getDisplayedOdds(odds, priceType, l10n),
          }
        : {
              value: EventUtils.getDisplayedOdds(odds, priceType, l10n),
              previousValue: baseOdds
                  ? EventUtils.getDisplayedOdds(baseOdds, priceType, l10n)
                  : undefined,
          };
};

const BetNotification = props => {
    const { bet, leg, mojitoTools } = props;
    const { config } = mojitoTools;
    const earlyPayoutPart = BetsUtils.getEarlyPayoutPart(leg);
    const hasEarlyPayout = Boolean(earlyPayoutPart);
    const bonusInfo = useMemo(
        () => ({ rules: earlyPayoutPart?.earlyPayout?.rules }),
        [earlyPayoutPart?.earlyPayout]
    );
    const isOddsBoostBet = BetsUtils.hasOddsBoost(bet) && bet.betType === BET_TYPE.SINGLE;

    if (!config.showBetLegNotification) {
        return null;
    }

    return (
        <FlexPane config={config.notificationContainer}>
            {hasEarlyPayout && (
                <BonusIndicator
                    class="ta-earlyPayoutIndicator"
                    config={config.earlyPayoutIndicator}
                    bonusType={BonusIndicatorTypes.TYPES.EARLY_PAYOUT}
                    bonusInfo={bonusInfo}
                    isAwarded={earlyPayoutPart.earlyPayout.awarded}
                />
            )}
            {isOddsBoostBet && <OddsBoostIndicator config={config.oddsBoostIndicator} bet={bet} />}
        </FlexPane>
    );
};

const LegStatusIcon = ({ status, config }) => {
    const iconStatus = BetViewUtils.getStatusIcon(status);
    return (
        <FlexPane config={config.legStatusIconContainer}>
            <BetStatusIcon config={config.legStatusIcon} status={iconStatus} />
        </FlexPane>
    );
};

const ExtraFields = ({ leg, bet, mojitoTools }) => {
    const { config } = mojitoTools;
    const firstLegPart = BetsUtils.getFirstLegPart(leg);
    const showExtraInfo = bet.hasOwnProperty('settled');
    const { eventStartTime } = bet.legs[0].legInfo;

    return (
        <FlexPane config={config.extraFields}>
            {showExtraInfo && (
                <LegDateTimeInfo
                    firstLegPart={firstLegPart}
                    mojitoTools={mojitoTools}
                    config={config}
                />
            )}

            {!showExtraInfo && (
                <EventStartDateTimeText config={config} eventStartTime={eventStartTime} />
            )}
        </FlexPane>
    );
};

const LegDateTimeInfo = ({ firstLegPart, mojitoTools, config }) =>
    config.showLegDateTime &&
    firstLegPart && (
        <FlexPane config={config.legDateTimeContainer} class="ta-legDateTimeContainer">
            <LegDateTimeInfoItem
                mojitoTools={mojitoTools}
                translation={'$BET_LEGS_DETAILS.STARTED'}
                dateTime={firstLegPart.started}
            />
            <LegDateTimeInfoItem
                mojitoTools={mojitoTools}
                translation={'$BET_LEGS_DETAILS.ENDED'}
                dateTime={firstLegPart.ended}
            />
            <LegDateTimeInfoItem
                mojitoTools={mojitoTools}
                translation={'$BET_LEGS_DETAILS.CONFIRMED'}
                dateTime={firstLegPart.completed}
            />
        </FlexPane>
    );

const EventStartDateTimeText = ({ config, eventStartTime }) =>
    config.showEventStartDateTime && (
        <Text config={config.eventStartDateTimeText} class="ta-eventStartDateTime">
            <DateTime config={config.eventStartDateTimeFormat} dateTime={eventStartTime} />
        </Text>
    );

const LegDateTimeInfoItem = ({ translation, dateTime, mojitoTools }) => {
    const { config, stringResolver } = mojitoTools;
    const label = stringResolver.resolveString(translation);
    return (
        <Text config={config.legDateInfoText}>
            {label}
            {!!dateTime.length && (
                <DateTime config={config.legDateTimeFormat} dateTime={dateTime} />
            )}
        </Text>
    );
};

const getSelectionId = leg => BetsUtils.getFirstLegPart(leg)?.selectionId;
const formatEventName = (startTime, eventName) =>
    EventFormatter.formatEventName(eventName, startTime);

export default BetLegsDetails;
